import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button } from "antd";
import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  OrderStepTrackerResponseItem,
  useGetLoggedUserQuery,
} from "shared/api";
import { Routes } from "shared/routers";
import { useWindowDimensions } from "shared/hooks";
import { GeneratePdfFilesContainer } from "./GeneratePdfFilesContainer";
import { MobileAndTabletProdPageHeader } from "./MobileAndTabletProdPageHeader";
import { OrderStatus } from "./OrderStatus";
import { OrderCodeInfo } from "./OrderCodeInfo";
import { ProcessStepTitle } from "./ProcessStepTitle";
import { AddSimpleTaskModal } from "./AddTaskForProductionPage";

interface ProdPageHeaderProps {
  decrementProcessStepIndex: () => void;
  incrementProcessStepIndex: () => void;
  orderStepTrackerResponseItem?: OrderStepTrackerResponseItem;
  closeProdPageModal: () => void;
  isLoadingOrderCodes: boolean;
  navigateOrder: (direction: "next" | "previous") => void;
  handleshowMaintenanceTableState: (value: boolean) => void;
  showMaintenanceIcon: boolean;
  productCardId: number;
  countOfTasksExecutions: number;
}

const ProdPageHeader: FC<ProdPageHeaderProps> = ({
  incrementProcessStepIndex,
  decrementProcessStepIndex,
  orderStepTrackerResponseItem,
  closeProdPageModal,
  isLoadingOrderCodes,
  navigateOrder,
  handleshowMaintenanceTableState,
  showMaintenanceIcon,
  productCardId,
  countOfTasksExecutions,
}) => {
  const { t } = useTranslation();
  const { isDesktop } = useWindowDimensions();

  const processStep = orderStepTrackerResponseItem?.process_step;
  const processStepName = processStep?.name;
  const processStepId = processStep?.id;
  const orderCode = orderStepTrackerResponseItem?.order?.order_code;

  const { data: currentUser } = useGetLoggedUserQuery();
  const [isAddModalWindowOpen, setIsAddModalWindowOpen] = useState(false);

  if (!isDesktop) {
    return (
      <MobileAndTabletProdPageHeader
        incrementProcessStepIndex={incrementProcessStepIndex}
        decrementProcessStepIndex={decrementProcessStepIndex}
        orderStepTrackerResponseItem={orderStepTrackerResponseItem}
        closeProdPageModal={closeProdPageModal}
        navigateOrder={navigateOrder}
        isLoadingOrderCodes={isLoadingOrderCodes}
      />
    );
  }

  return (
    <div
      className="p-2 bg-blue-primary mb-1 brightness-150 w-full flex items-center justify-between"
      style={{ position: "sticky", top: 0, width: "100%", zIndex: 1000 }}
    >
      <div className="flex gap-x-2">
        <OrderCodeInfo
          navigateOrder={navigateOrder}
          orderCode={orderCode}
          isLoadingOrderCodes={isLoadingOrderCodes}
        />

        <OrderStatus
          orderStepTrackerResponseItem={orderStepTrackerResponseItem}
        />

        <ProcessStepTitle
          decrementProcessStepIndex={decrementProcessStepIndex}
          incrementProcessStepIndex={incrementProcessStepIndex}
          processStepId={processStepId}
          processStepName={processStepName}
        />
      </div>

      <GeneratePdfFilesContainer
        orderStepTrackerResponseItem={orderStepTrackerResponseItem}
      />

      <div className="flex items-center justify-center gap-3">
        {showMaintenanceIcon && (
          <>
            <div
              onClick={() => setIsAddModalWindowOpen(true)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  setIsAddModalWindowOpen(true);
                }
              }}
              role="button"
              tabIndex={0}
              className="cursor-pointer"
            >
              <img width={40} height={40} src="/plus-icon.png" alt="" />
            </div>

            <div
              className="relative cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => handleshowMaintenanceTableState(true)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  handleshowMaintenanceTableState(true);
                }
              }}
            >
              {countOfTasksExecutions > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] px-[6px] text-xs bg-red-600 text-white rounded-full flex items-center justify-center leading-none z-10">
                  {countOfTasksExecutions > 99 ? "99+" : countOfTasksExecutions}
                </span>
              )}

              <img
                width={40}
                height={40}
                src="/product-card-maintance-icon.png"
                alt=""
              />
            </div>
          </>
        )}
        <Link to={Routes.orders.url}>
          <Button
            onClick={closeProdPageModal}
            type="default"
            className="
           !text-white !text-bold mr-2
           hover:!bg-white hover:!text-blue-primary
            flex items-center justify-center h-[70%]
          "
          >
            <ArrowLeftOutlined />
            {t("orders.back-to-orders")}
          </Button>
        </Link>
      </div>

      <AddSimpleTaskModal
        open={isAddModalWindowOpen}
        onClose={() => setIsAddModalWindowOpen(false)}
        currentUserId={currentUser?.id ?? 0}
        currentProductCardId={productCardId}
      />
    </div>
  );
};

export { ProdPageHeader };
